import {
  AppName,
  AppVersionData,
  getAppDisplayName,
  MAINTENANCE_DOC,
  MaintenanceInfo,
  SETTINGS,
  version,
} from '@hb/shared'
import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { AppData } from '../types/app'
import { useFetchData } from './db'

const getIsUnderMaintenance = (maintenanceInfo: MaintenanceInfo | null) => {
  if (!maintenanceInfo) return false
  return new Date(maintenanceInfo.startTime).getTime() <= Date.now()
}
const useIsUnderMaintenance = (maintenanceInfo: MaintenanceInfo | null) => {
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(
    getIsUnderMaintenance(maintenanceInfo),
  )

  useEffect(() => {
    setIsUnderMaintenance(getIsUnderMaintenance(maintenanceInfo))
    if (!maintenanceInfo) return
    const timeUntilStart = new Date(maintenanceInfo.startTime).getTime() - Date.now()
    if (timeUntilStart <= 0) return
    const timeout = setTimeout(() => {
      setIsUnderMaintenance(true)
    }, timeUntilStart)
    return () => clearTimeout(timeout)
  }, [maintenanceInfo])

  return isUnderMaintenance
}
const useAppData = (appName: AppName): AppData => {
  const { useDocument } = useFetchData()
  const appDataCollection = useMemo(() => `apps/${appName}/${version}`, [appName])
  const appVersion = useDocument<AppVersionData>(appDataCollection, 'current')
  const appDisplayName = useMemo(() => getAppDisplayName(appName), [appName])
  const { data: maintenanceInfo } = useDocument<MaintenanceInfo>(SETTINGS, MAINTENANCE_DOC)
  const isUnderMaintenance = useIsUnderMaintenance(maintenanceInfo)
  return {
    appName,
    appVersion,
    appDisplayName,
    isUnderMaintenance,
    maintenanceInfo,
  }
}

export const AppContext = React.createContext<AppData>({
  appName: 'NONE',
  appDisplayName: 'NONE',
  isUnderMaintenance: false,
  maintenanceInfo: null,
  appVersion: {
    data: null,
    loading: false,
    error: null,
    set: async () => {},
    update: async () => {},
  },
})

export const useApp = () => useContext(AppContext)

export const AppProvider = ({ children, appName }: { children: ReactNode; appName: AppName }) => {
  const appData = useAppData(appName)

  return <AppContext.Provider value={appData}>{children}</AppContext.Provider>
}
